import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import {
    Grid,
    CardHeader,
    CardContent,
    Typography,
    Box,
    Button,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Alert,
} from '@mui/material';
import {
    Visibility as VisibilityIcon,
    Download as DownloadIcon,
    Lock as LockIcon,
    PictureAsPdf as PdfIcon,
    CalendarMonth
} from '@mui/icons-material';
import { useSelector, useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { GeneralCard, Loader } from '../../../../Components';
import { styled } from '@mui/material/styles';
import dayjs from 'dayjs';
import {cleanName} from "../../../../Setup/Config/formats";
import {lockRoster, retryPDFGeneration, fetchCurrentRoster} from "../Redux/Action";


// Styled Components
const StyledTableContainer = styled(TableContainer)(({ theme }) => ({
    '& .MuiTableCell-root': {
        padding: theme.spacing(1),
        fontSize: '0.875rem',
        textAlign: 'center',
        borderRight: '1px solid',
        borderColor: theme.palette.divider,
        '&:last-child': {
            borderRight: 'none'
        }
    },
    '& .MuiTableHead-root .MuiTableCell-root': {
        backgroundColor: theme.palette.grey[50],
        fontWeight: 600,
        color: theme.palette.text.primary
    },
    '& .status-cell': {
        minWidth: 100,
    },
    '& .status-off': {
        color: `${theme.palette.error.main} !important`,
        fontWeight: 600
    },
    '& .user-cell': {
        textAlign: 'left',
        minWidth: 200
    },
    '& .contact-cell': {
        textAlign: 'center',
        minWidth: 150,
        maxWidth: 200
    },
    '& .event-cell': {
        textAlign: 'center',
        minWidth: 150,
        maxWidth: 200
    },
    '& .date-cell': {
        width: '80px',
        minWidth: '80px',
        maxWidth: '80px'
    }
}));

const ActionButton = styled(Button)(({ theme, variant }) => ({
    margin: theme.spacing(0.5),
    minWidth: 120,
    ...(variant === 'draft' && {
        backgroundColor: theme.palette.warning.main,
        color: theme.palette.warning.contrastText,
        '&:hover': {
            backgroundColor: theme.palette.warning.dark
        }
    })
}));

export const Generate = () => {
    
    const dispatch = useDispatch();
    const navigate = useNavigate();
    
    // State Management
    const [isLoading, setIsLoading] = useState(false);
    const [isRetrying, setIsRetrying] = useState(false); // NEW: Track retry state
    const [, setErrors] = useState([]);
    const [, setSuccess] = useState(false);
    const [, setMessage] = useState('');
    
    // Ref for polling interval
    const pollingInterval = useRef(null);

    // Redux Selectors
    const departments = useSelector((store) => store.app.config.departments);
    const currentUser = useSelector((store) => store.auth.user);
    const roster = useSelector((store) => store.rosters.selected);
    const items = useSelector((store) => store.rosters.selected.entries);

    // Current week calculation (always current week)
    const currentWeek = useMemo(() => {
        const startOfWeek = dayjs().startOf('week').add(1, 'day'); // Monday
        const endOfWeek = dayjs().endOf('week').add(1, 'day'); // Sunday
        return { start: startOfWeek, end: endOfWeek };
    }, []);

    // Computed roster status from actual data
    const rosterStatus = useMemo(() => {
        return roster?.status?.name || 'DRAFT';
    }, [roster]);


    // Week days generation - Show only days with roster entries
    const weekDays = useMemo(() => {
        if (!items || items.length === 0) {
            return [];
        }
        
        // Extract unique dates from roster entries
        const uniqueDates = [...new Set(items.map(item => item.roster_date))];
        
        // Convert to dayjs objects and sort
        const days = uniqueDates
            .map(date => dayjs(date))
            .sort((a, b) => a.isAfter(b) ? 1 : -1);
        
        return days;
    }, [items]);

    // Filter departments based on user role
    const availableDepartments = useMemo(() => {
        if (currentUser?.role_type === 'MANAGER') {
            return departments.filter(dept => dept.manager_id === currentUser.id);
        }
        return departments;
    }, [departments, currentUser]);

    // Fetch roster data for current week
    const fetchData = useCallback(async () => {
        setIsLoading(true);
        await fetchCurrentRoster({ dispatch }).then((res) => {
            setIsLoading(false);
            setErrors(res.errors);
            setSuccess(res.success);
            setMessage(res.message);
        });
    }, [dispatch]);

    useEffect(() => {
        fetchData();
    }, [fetchData]);

    // Handle roster generation (DRAFT to LOCKED)
    const handleGenerateRoster = async () => {
        setIsLoading(true);
        await lockRoster({ dispatch, data: {} }).then((res) => {
            setIsLoading(false);
            if(res.success){
                // Refresh roster data to get updated status
                fetchData();
            }
            setErrors(res.errors);
            setSuccess(res.success);
            setMessage(res.message);
        });
    };

    // Handle PDF access - SharePoint link from roster document
    const handleViewPDF = () => {
        if (!roster?.avatar_url) {
            setErrors(['SharePoint PDF link not found. Please regenerate the roster.']);
            return;
        }
        
        window.open(roster.avatar_url, '_blank');
    };

    // Handle PDF generation retry (different from initial generation)
    const handleRetryPDFGeneration = async () => {
        setIsRetrying(true);
        setIsLoading(true);
        
        await retryPDFGeneration({ dispatch, data: { roster_id: roster.id } }).then((res) => {
            setIsLoading(false);
            if(res.success){
                // Refresh roster data to get updated status
                // Polling will automatically start when status becomes LOCKED
                fetchData();
            }
            setErrors(res.errors);
            setSuccess(res.success);
            setMessage(res.message);
        });
        
    };

    // PDF status polling function to check roster status changes
    const startPDFStatusPolling = useCallback(() => {
        if (!roster?.id) return;
        
        // Clear any existing interval
        if (pollingInterval.current) {
            clearInterval(pollingInterval.current);
        }
        
        // Start polling every 3 seconds
        pollingInterval.current = setInterval(async () => {
            try {
                // Fetch fresh roster data to check status
                await fetchData();
                
                // Stop polling if status is no longer LOCKED (processing)
                if (rosterStatus !== 'LOCKED') {
                    stopPDFStatusPolling();
                }
            } catch (error) {
                // Stop polling on error
                stopPDFStatusPolling();
            }
        }, 3000); // Check every 3 seconds
    // eslint-disable-next-line react-hooks/exhaustive-deps -- stopPDFStatusPolling is stable
    }, [roster?.id, rosterStatus, fetchData]);
    
    // Stop PDF status polling
    const stopPDFStatusPolling = useCallback(() => {
        if (pollingInterval.current) {
            clearInterval(pollingInterval.current);
            pollingInterval.current = null;
        }
    }, []);
    
    // Auto-start/stop polling based on roster status
    useEffect(() => {
        if (rosterStatus === 'LOCKED') {
            startPDFStatusPolling();
        } else {
            stopPDFStatusPolling();
        }
        
        // Cleanup on unmount
        return () => {
            stopPDFStatusPolling();
        };
    }, [rosterStatus, startPDFStatusPolling, stopPDFStatusPolling]);


    // Format time from HH:mm:ss to HHmm format
    const formatTime = (timeString) => {
        if (!timeString) return '';
        return timeString.substring(0, 5).replace(':', '');
    };

    return (
        <Grid container spacing={3}>
            <Grid item xs={12}>
                <Box justifyContent="space-between" display="flex" alignItems="center">
                    <Typography variant="h4" fontWeight={600}>
                        Roster Generation
                    </Typography>
                    <Button
                        variant="outlined"
                        onClick={() => navigate('/roster/manage')}
                        startIcon={<CalendarMonth />}
                    >
                        Back to Calendar
                    </Button>
                </Box>
            </Grid>

            {/* Merged Roster Preview & Actions */}
            <Grid item xs={12}>
                <GeneralCard>
                    <CardHeader
                        title="Current Week Roster"
                        subheader={`Week of ${currentWeek.start.format('MMM DD')} - ${currentWeek.end.format('MMM DD, YYYY')} | Status: ${
                            rosterStatus === 'DRAFT' ? 'Draft - Ready for Generation' :
                            rosterStatus === 'LOCKED' && !isRetrying ? 'Locked - PDF Generation in Progress' :
                            rosterStatus === 'LOCKED' && isRetrying ? 'Locked - Retrying PDF Generation' :
                            rosterStatus === 'PDF_GENERATED' ? 'PDF Generated - Ready for Download' :
                            rosterStatus === 'PDF_FAILED' ? 'PDF Generation Failed - Retry Available' :
                            'Unknown Status'
                        }`}
                        action={
                            <Box display="flex" gap={2} flexWrap="wrap" alignItems="center">
                                {rosterStatus === 'DRAFT' ? (
                                    <ActionButton
                                        variant="draft"
                                        onClick={handleGenerateRoster}
                                        disabled={isLoading}
                                        startIcon={<LockIcon />}
                                    >
                                        Generate & Lock Roster
                                    </ActionButton>
                                ) : rosterStatus === 'LOCKED' ? (
                                    <Button
                                        variant="outlined"
                                        disabled={true}
                                        startIcon={<PdfIcon />}
                                    >
                                        {isRetrying ? 'Retrying PDF Generation...' : 'PDF Generation in Progress...'}
                                    </Button>
                                ) : rosterStatus === 'PDF_GENERATED' ? (
                                    <Button
                                        variant="contained"
                                        color="primary"
                                        onClick={handleViewPDF}
                                        startIcon={<VisibilityIcon />}
                                    >
                                        View PDF
                                    </Button>
                                ) : rosterStatus === 'PDF_FAILED' ? (
                                    <Button
                                        variant="outlined"
                                        color="error"
                                        onClick={handleRetryPDFGeneration}
                                        disabled={isLoading}
                                        startIcon={<PdfIcon />}
                                    >
                                        Retry PDF Generation
                                    </Button>
                                ) : null}
                            </Box>
                        }
                        avatar={<Loader isLoading={isLoading} type="linear" />}
                    />
                    <CardContent>


                        {/* Status Alerts */}
                        {rosterStatus === 'DRAFT' && (
                            <Grid item xs={12}>
                                <Alert severity="info" icon={<LockIcon />}>
                                    <Typography variant="body1" fontWeight={600}>
                                        Roster in Draft Mode
                                    </Typography>
                                    <Typography variant="body2">
                                        Your roster is ready for generation. Click "Generate & Lock Roster" to finalize it.
                                    </Typography>
                                </Alert>
                            </Grid>
                        )}

                        {rosterStatus === 'LOCKED' && !isRetrying && (
                            <Grid item xs={12}>
                                <Alert severity="info" icon={<PdfIcon />}>
                                    <Typography variant="body1" fontWeight={600}>
                                        Roster Locked! PDF Generation in Progress
                                    </Typography>
                                    <Typography variant="body2">
                                        Your roster has been locked successfully. PDF generation is running in the background. 
                                        You'll be notified when it's ready for download.
                                    </Typography>
                                </Alert>
                            </Grid>
                        )}

                        {rosterStatus === 'LOCKED' && isRetrying && (
                            <Grid item xs={12}>
                                <Alert severity="warning" icon={<PdfIcon />}>
                                    <Typography variant="body1" fontWeight={600}>
                                        Retrying PDF Generation...
                                    </Typography>
                                    <Typography variant="body2">
                                        PDF generation failed previously. We're retrying the process. Please wait...
                                    </Typography>
                                </Alert>
                            </Grid>
                        )}

                        {rosterStatus === 'PDF_GENERATED' && (
                            <Grid item xs={12}>
                                <Alert severity="success" icon={<DownloadIcon />}>
                                    <Typography variant="body1" fontWeight={600}>
                                        PDF Ready for Download!
                                    </Typography>
                                    <Typography variant="body2">
                                        Your roster PDF has been generated successfully. Click "Download PDF" to save it.
                                    </Typography>
                                </Alert>
                            </Grid>
                        )}

                        {rosterStatus === 'PDF_FAILED' && (
                            <Grid item xs={12}>
                                <Alert severity="error" icon={<PdfIcon />}>
                                    <Typography variant="body1" fontWeight={600}>
                                        PDF Generation Failed!
                                    </Typography>
                                    <Typography variant="body2">
                                        PDF generation failed due to an error. Click "Retry PDF Generation" to try again.
                                    </Typography>
                                </Alert>
                            </Grid>
                        )}

                        {/* Roster Table */}
                        <StyledTableContainer sx={{ mb: 3 }}>
                            <Table size="small">
                                <TableHead>
                                    <TableRow>
                                        <TableCell className="user-cell">NAME</TableCell>
                                        <TableCell className="duty-cell">CALL SIGN / DUTY AREA & SERVICES</TableCell>
                                        <TableCell align='center'>Event</TableCell>
                                        <TableCell className="contact-cell">Contact No</TableCell>
                                        {weekDays.map((day) => (
                                            <TableCell key={day.format('YYYY-MM-DD')} className="date-cell">
                                                <Box textAlign="center">
                                                    <Typography variant="caption" display="block">
                                                        {day.format('ddd')}
                                                    </Typography>
                                                    <Typography variant="body2" fontWeight={600}>
                                                        {day.format('DD')}
                                                    </Typography>
                                                </Box>
                                            </TableCell>
                                        ))}
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {(() => {
                                        // Get all departments from config
                                        const allDepartments = availableDepartments.map(dept => dept.department_name).sort();
                                        
                                        // Group roster entries by user and then by department
                                        const userRosterMap = {};
                                        if (items && items.length > 0) {
                                            items.forEach(entry => {
                                                const userId = entry.user_id;
                                                if (!userRosterMap[userId]) {
                                                    userRosterMap[userId] = {
                                                        user: entry.user,
                                                        rosters: {}
                                                    };
                                                }
                                                // Map roster entries by date
                                                userRosterMap[userId].rosters[entry.roster_date] = {
                                                    status: entry.status,
                                                    start_time: entry.start_time,
                                                    end_time: entry.end_time
                                                };
                                            });
                                        }

                                        // Group users by department
                                        const departmentGroups = {};
                                        Object.values(userRosterMap).forEach(userData => {
                                            const deptName = userData.user.department_id ? 
                                                availableDepartments.find(d => d.id === userData.user.department_id)?.department_name || 'No Department' : 
                                                'No Department';
                                            
                                            if (!departmentGroups[deptName]) {
                                                departmentGroups[deptName] = [];
                                            }
                                            departmentGroups[deptName].push(userData);
                                        });

                                        const rows = [];
                                        
                                        // Create rows for ALL departments (ascending order)
                                        allDepartments.forEach(deptName => {
                                            // Department header row
                                            rows.push(
                                                <TableRow key={`dept-${deptName}`} sx={{ backgroundColor: 'grey.100' }}>
                                                    <TableCell 
                                                        colSpan={weekDays.length + 4} 
                                                        sx={{ 
                                                            textAlign: 'left!important', 
                                                            fontWeight: 800,
                                                            fontSize: '0.875rem!important',
                                                            py: 2,
                                                            borderBottom: '2px solid',
                                                            borderColor: 'divider'
                                                        }}
                                                    >
                                                        {deptName}
                                                    </TableCell>
                                                </TableRow>
                                            );

                                            // User rows for this department (if any)
                                            const users = departmentGroups[deptName] || [];
                                            if (users.length > 0) {
                                                users.forEach(userData => (
                                                    rows.push(
                                                        <TableRow key={userData.user.id} hover>
                                                            <TableCell className="user-cell">
                                                                <Typography variant="subtitle2" fontWeight={600}>
                                                                    {userData.user.first_name} {userData.user.last_name}
                                                                </Typography>
                                                            </TableCell>
                                                            <TableCell className="duty-cell">
                                                                <Typography variant="body2" color="text.secondary">
                                                                    {userData.user.designation || 'Employee'}
                                                                </Typography>
                                                            </TableCell>
                                                            <TableCell align='center'>
                                                                <Typography variant="body2" color="text.secondary">
                                                                    {userData.user.event || '-'}
                                                                </Typography>
                                                            </TableCell>
                                                            <TableCell className="contact-cell">
                                                                <Typography variant="body2" color="text.secondary">
                                                                    {userData.user.mobile || 'N/A'}
                                                                </Typography>
                                                            </TableCell>
                                                            {weekDays.map((day) => {
                                                                const dateKey = day.format('YYYY-MM-DD');
                                                                const rosterEntry = userData.rosters[dateKey];
                                                                const status = rosterEntry?.status?.name || '-';
                                                                return (
                                                                    <TableCell key={dateKey} className={`status-cell status-${status.toLowerCase().replace('_', '-')}`}>
                                                                        {status === '-' ? (
                                                                            <Typography variant="body2" color="text.secondary">
                                                                                -
                                                                            </Typography>
                                                                        ) : status === 'ON_DUTY' && rosterEntry?.start_time && rosterEntry?.end_time ? (
                                                                            <Typography variant="body2" color="primary" sx={{ whiteSpace: 'nowrap' }}>
                                                                                {formatTime(rosterEntry.start_time)} - {formatTime(rosterEntry.end_time)}
                                                                            </Typography>
                                                                        ) : (
                                                                            <Typography variant="body2" fontWeight={500}>
                                                                                {cleanName(status)}
                                                                            </Typography>
                                                                        )}
                                                                    </TableCell>
                                                                );
                                                            })}
                                                        </TableRow>
                                                    )
                                                ));
                                            } else {
                                                // No users in this department - show empty row
                                                rows.push(
                                                    <TableRow key={`empty-${deptName}`}>
                                                        <TableCell className="user-cell">
                                                            <Typography variant="body2" color="text.secondary" fontStyle="italic">
                                                                No employees assigned
                                                            </Typography>
                                                        </TableCell>
                                                        <TableCell className="duty-cell" align='center'>-</TableCell>
                                                        <TableCell className="contact-cell" align='center'>-</TableCell>
                                                        <TableCell className="event-cell" align='center'>-</TableCell>
                                                        {weekDays.map((day) => (
                                                            <TableCell key={day.format('YYYY-MM-DD')} className="status-cell">-</TableCell>
                                                        ))}
                                                    </TableRow>
                                                );
                                            }
                                        });

                                        return rows;
                                    })()}
                                </TableBody>
                            </Table>
                        </StyledTableContainer>


                    </CardContent>
                </GeneralCard>
            </Grid>
        </Grid>
    );
};
